import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import { validateCheckoutInput } from "../../../utils/CheckoutErrorHandler";
import {
  initiateGuestOrder,
  createWalletOrder,
  initiateUserStripeOrder,
  initiateGuestMultipleOrder,
  createWalletMultipleOrder,
  initiateUserStripeMultipleOrder,
} from "../../../services/OrderService";
import { markNewsletter } from "../../../services/UserService";
import { validateVoucher as validateVoucherApi } from "../../../services/VoucherService";
import { purchaseTopUp } from "../../../services/TopUpService";
import { checkPromocodeAvailability } from "../../../services/PromocodeService";

import { fetchPackageById } from "../../../services/PackagesService";

import AuthPanel from "../AuthPanel/AuthPanel";
import OrderSummary from "../OrderSummary/OrderSummary";
import PaymentModal from "../PaymentModal/PaymentModal";
import StatusModal from "../StatusModal/StatusModal";
import { trackAnalyticsEvent } from "../../../analytics/analytics";
import "../CheckoutPanel/CheckoutPanel.css";

const CheckoutLayout = ({ status }) => {
  const navigate = useNavigate();
  const { items, clear } = useCart();
  const { token, wallet, walletCurrency, loading: authLoading, newsletter, userId } = useContext(AuthContext);
  const activeCurrency = walletCurrency || "EUR";
  const isLoggedIn = !!token;

  const [newsletterOptInGuest, setNewsletterOptInGuest] = useState(false);
  const showNewsletterCheckbox = !isLoggedIn || !newsletter;
  const [turnstileToken, setTurnstileToken] = useState("");
  const [shouldShowTurnstile, setShouldShowTurnstile] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [speedConfirmed, setSpeedConfirmed] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [packages, setPackages] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [promocodeData, setPromocodeData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [finalStatus, setFinalStatus] = useState(status);

  const [frozenItems, setFrozenItems] = useState([]);

  useEffect(() => {
    if (finalStatus !== "success" && items.length > 0) {
      setFrozenItems(items);
    }
  }, [items, finalStatus]);

  const displayItems = finalStatus === "success" ? frozenItems : items;
  const totalUnits = displayItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const hasMultiplePackages = totalUnits > 1 || displayItems.length > 1;

  useEffect(() => {
    if (!status) return;

    setFinalStatus(status);

    if (status === "success" || status === "canceled") {
      setShowStatusModal(true);
    }
  }, [status]);

  useEffect(() => {
    if (finalStatus === "success" || finalStatus === "canceled") {
      setShowStatusModal(true);
    }
  }, [finalStatus]);

  const savePendingStripePurchase = (itemsToSave, promo, curr) => {
    const rawPrice = (itemsToSave || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
    const discountAmount = promo?.amount || 0;
    const finalValue = Math.max(0, rawPrice - discountAmount);

    const pendingData = {
      value: finalValue,
      price: rawPrice,
      currency: curr,
      coupon: promo?.code || "",
      payment_type: "card",
      purchase_type: "esim_purchase",
      items: (itemsToSave || []).map((item) => ({
        item_id: item.internal_package_id || item.id,
        item_name: item.name || "eSIM Package",
        price: item.price || 0,
        quantity: item.quantity || 1,
      })),
      createdAt: Date.now(),
    };

    try {
      localStorage.setItem("pending_stripe_purchase", JSON.stringify(pendingData));
    } catch (e) {
      console.error("Failed to save pending_stripe_purchase:", e);
    }
  };

  useEffect(() => {
    if (status === "success") {
      let cachedPurchase = null;
      try {
        const raw = localStorage.getItem("pending_stripe_purchase");
        if (raw) {
          cachedPurchase = JSON.parse(raw);
          localStorage.removeItem("pending_stripe_purchase");
        }
      } catch (e) {
        console.error("Failed to parse pending_stripe_purchase:", e);
      }

      const transactionId = new URLSearchParams(window.location.search).get("session_id") || Date.now();

      if (cachedPurchase) {
        trackAnalyticsEvent.purchase({
          ...cachedPurchase,
          transactionId,
        });
      } else {
        const rawPrice = (displayItems || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
        const discountAmount = promocodeData?.amount || 0;
        const finalValue = Math.max(0, rawPrice - discountAmount);

        trackAnalyticsEvent.purchase({
          transactionId,
          value: finalValue,
          price: rawPrice,
          currency: activeCurrency,
          coupon: promocodeData?.code || "",
          payment_type: "card",
          purchase_type: "esim_purchase",
          items: (displayItems || []).map((item) => ({
            item_id: item.internal_package_id || item.id,
            item_name: item.name || "eSIM Package",
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        });
      }
    } else if (status === "canceled") {
      localStorage.removeItem("pending_stripe_purchase");
    }
  }, [status]);

  const isTopUpOrder = useMemo(() => {
    return items.some((item) => item.type === "topup");
  }, [items]);

  const packageIds = useMemo(() => {
    const ids = (displayItems || []).map((it) => {
      const id = it.internal_package_id || it.id;
      return id;
    });
    const stringIds = ids.map((x) => String(x));
    return Array.from(new Set(stringIds));
  }, [displayItems]);

  const currency = (value, cur = walletCurrency) => {
    if (value == null) return "";
    return `${value.toFixed(2)} ${cur}`;
  };

  const validationError = useMemo(() => {
    const mail = (email || "").trim();
    return !isLoggedIn ? validateCheckoutInput(mail) : null;
  }, [email, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;

    const mail = (email || "").trim();
    const isEmailValid = !validateCheckoutInput(mail);

    if (isEmailValid && !shouldShowTurnstile) {
      setShouldShowTurnstile(true);
    }
  }, [email, isLoggedIn, shouldShowTurnstile]);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setErrorMessage("");
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
    setErrorMessage("Security verification expired. Please try again.");
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setErrorMessage("Security verification failed. Please try again.");
  }, []);

  const handleGuestOrder = async () => {
    if (!turnstileToken) {
      setErrorMessage("Please complete the security verification.");
      return;
    }

    try {
      if (isTopUpOrder) {
        setErrorMessage("Top-Up purchases require login. Please sign in.");
        return;
      }

      setOrderLoading(true);
      let orderData;

      if (hasMultiplePackages) {
        orderData = await initiateGuestMultipleOrder({
          items: displayItems,
          userId: email,
          frontendUrl: `${window.location.origin}/cart`,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
          turnstileToken,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateGuestOrder({
          internalPackageId: internalPackageId,
          userId: email,
          frontendUrl: `${window.location.origin}/cart`,
          currency: activeCurrency,
          newsletter: newsletterOptInGuest,
          turnstileToken,
        });
      }

      const stripeUrl = orderData.redirect_url;
      if (stripeUrl) {
        savePendingStripePurchase(displayItems, promocodeData, activeCurrency);
        window.location.href = stripeUrl;
      }
    } catch (error) {
      console.error("❌ Error:", error);

      const detail = error.response?.data?.detail || "";
      const detailText = Array.isArray(detail) ? detail.join(" ").toLowerCase() : String(detail).toLowerCase();

      const isTurnstileError =
        detailText.includes("turnstile") ||
        detailText.includes("timeout-or-duplicate") ||
        detailText.includes("invalid-input-response");

      if (isTurnstileError) {
        setTurnstileToken("");
        setErrorMessage("Please complete the security verification again.");
      } else {
        setErrorMessage(error.response?.data?.detail || "Payment failed");
      }
    } finally {
      setOrderLoading(false);
    }
  };

  const handleWalletOrder = async () => {
    if (!wallet?.id) {
      setErrorMessage("Wallet not found");
      setFinalStatus("canceled");
      return false;
    }

    try {
      setOrderLoading(true);
      if (isTopUpOrder) {
        const cartItem = displayItems[0];
        let orderData;

        orderData = await purchaseTopUp({
          iccid: cartItem.target_iccid,
          walletId: wallet.id,
          packageId: cartItem.internal_package_id,
          packageCode: cartItem.package_code,
        });

        trackAnalyticsEvent.esimTopUp({
          transactionId: orderData?.transaction_id || null,
          value: cartItem?.price || 0,
          currency: activeCurrency,
          coupon: "",
          payment_type: "wallet",
          purchase_type: "esim_topup",
          item_id: cartItem?.internal_package_id || cartItem?.id || "",
          item_name: cartItem?.name || "eSIM Top-Up",
          price: cartItem?.price || 0,
          quantity: 1,
        });
      } else {
        let orderData;
        if (hasMultiplePackages) {
          orderData = await createWalletMultipleOrder({
            items: displayItems,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        } else {
          const cartItem = displayItems[0];
          const internalPackageId = cartItem.internal_package_id || cartItem.id;

          orderData = await createWalletOrder({
            internalPackageId: internalPackageId,
            walletId: wallet.id,
            promocode: promocodeData ? promocodeData.code : null,
          });
        }

        const rawPrice = (displayItems || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
        const discountAmount = promocodeData?.amount || 0;
        const finalValue = Math.max(0, rawPrice - discountAmount);

        trackAnalyticsEvent.esimOrder({
          transactionId: orderData?.transaction_id || null,
          value: finalValue,
          price: rawPrice,
          currency: activeCurrency,
          coupon: promocodeData ? promocodeData.code : "",
          payment_type: "wallet",
          purchase_type: "esim_order",
          items: (displayItems || []).map((item) => ({
            item_id: item.internal_package_id || item.id,
            item_name: item.name || "eSIM Package",
            price: item.price || 0,
            quantity: item.quantity || 1,
          })),
        });
      }
      setFinalStatus("success");
      return true;
    } catch (error) {
      console.error("❌ Wallet payment failed:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
      setFinalStatus("canceled");
      return false;
    } finally {
      setOrderLoading(false);
    }
  };

  const handleUserStripeOrder = async () => {
    try {
      setOrderLoading(true);
      let orderData;

      if (hasMultiplePackages) {
        orderData = await initiateUserStripeMultipleOrder({
          items: displayItems,
          walletId: wallet?.id,
          frontendUrl: `${window.location.origin}/cart`,
          promocode: promocodeData ? promocodeData.code : null,
        });
      } else {
        const cartItem = displayItems[0];
        const internalPackageId = cartItem.internal_package_id || cartItem.id;

        orderData = await initiateUserStripeOrder({
          internalPackageId: internalPackageId,
          walletId: wallet?.id,
          frontendUrl: `${window.location.origin}/cart`,
          promocode: promocodeData ? promocodeData.code : null,
        });
      }

      const rawPrice = (displayItems || []).reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1), 0);
      const discountAmount = promocodeData?.amount || 0;
      const finalValue = Math.max(0, rawPrice - discountAmount);

      trackAnalyticsEvent.esimOrder({
        transactionId: orderData?.transaction_id || null,
        value: finalValue,
        price: rawPrice,
        currency: activeCurrency,
        coupon: promocodeData ? promocodeData.code : "",
        payment_type: "stripe",
        purchase_type: "esim_order",
        items: (displayItems || []).map((item) => ({
          item_id: item.internal_package_id || item.id,
          item_name: item.name || "eSIM Package",
          price: item.price || 0,
          quantity: item.quantity || 1,
        })),
      });
      const stripeUrl = orderData.redirect_url;
      if (stripeUrl) {
        savePendingStripePurchase(displayItems, promocodeData, activeCurrency);
        window.location.href = stripeUrl;
      }
    } catch (error) {
      console.error("❌ User Stripe order failed:", error);
      setErrorMessage(error.response?.data?.detail || "Payment failed");
    } finally {
      setOrderLoading(false);
    }
  };

  const handlePlaceOrder = () => {
    if (!isLoggedIn && isTopUpOrder) {
      setErrorMessage("Top-Up purchases require login. Please sign in.");
      return;
    }
    if (!isLoggedIn && validationError) {
      setErrorMessage(validationError);
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (method) => {
    if (isLoggedIn) {
      if (method === "wallet") {
        const success = await handleWalletOrder();
        setShowPaymentModal(false);
      } else if (method === "stripe") {
        await handleUserStripeOrder();
        setShowPaymentModal(false);
      } else {
        await handleGuestOrder();
        setShowPaymentModal(false);
      }
    } else {
      await handleGuestOrder();
      setShowPaymentModal(false);
    }
  };

  const handleApplyPromocode = async (code) => {
    const data = await validateVoucherApi(code);

    if (data.available && data.promocode?.type === "sell_discount") {
      const promo = { code: data.promocode.code, type: data.promocode.type, amount: data.promocode.amount };
      setPromocodeData(promo);
    } else {
      setPromocodeData(null);
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      if (!packageIds || packageIds.length === 0) {
        setPackages([]);
        return;
      }

      try {
        const results = await Promise.all(
          packageIds.map(async (pid) => {
            try {
              const data = await fetchPackageById(pid, walletCurrency);
              return data;
            } catch {
              const cartItem = displayItems.find((it) => it.internal_package_id || it.id === String(pid));
              if (!cartItem) return null;

              return {
                id: cartItem.internal_package_id || cartItem.id,
                name: cartItem.name,
                price: cartItem.price,
                currency: walletCurrency,
                country_id: cartItem.country_id,
                region_id: cartItem.region_id,
                gigabytes: cartItem.gigabytes,
                duration: cartItem.duration,
                top_up_available: cartItem.top_up_available,
              };
            }
          }),
        );

        const next = results.filter(Boolean);

        if (!cancelled) setPackages(next);
      } catch (e) {
        if (!cancelled) setPackages([]);
      }
    };

    loadPackages();

    return () => {
      cancelled = true;
    };
  }, [packageIds, walletCurrency]);

  const totals = useMemo(() => {
    const subtotal = (displayItems || []).reduce((acc, cartItem) => {
      const pid = cartItem.internal_package_id || cartItem.id;
      const pkg = packages.find((p) => String(p.id) === pid);
      if (!pkg) return acc;
      const qty = cartItem.quantity || 1;
      return acc + (pkg.price || 0) * qty;
    }, 0);

    const taxes = subtotal * 0.0;
    return { subtotal, taxes, total: subtotal + taxes };
  }, [displayItems, packages]);

  if (authLoading) return null;

  return (
    <section className="checkout">
      {isLoggedIn ? (
        <div className="checkout__center-column">
          <OrderSummary
            packages={packages}
            currency={currency}
            totals={totals}
            items={displayItems}
            onPlaceOrder={handlePlaceOrder}
            promocodeData={promocodeData}
            onApplyPromocode={handleApplyPromocode}
            hidePromocode={isTopUpOrder}
            newsletterOptIn={newsletterOptIn}
            onNewsletterOptInChange={setNewsletterOptIn}
            showNewsletterCheckbox={showNewsletterCheckbox}
            speedConfirmed={speedConfirmed}
            onSpeedConfirmChange={setSpeedConfirmed}
          />
        </div>
      ) : (
        <>
          <div className="checkout__left-column">
            <AuthPanel
              email={email}
              errorMessage={errorMessage}
              onEmailChange={(e) => setEmail(e.target.value)}
              onErrorClose={() => setErrorMessage("")}
              onLoginClick={() => navigate("/sign-in")}
              turnstileSiteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              showTurnstile={shouldShowTurnstile}
              onTurnstileVerify={handleTurnstileVerify}
              onTurnstileExpire={handleTurnstileExpire}
              onTurnstileError={handleTurnstileError}
            />
          </div>
          <div className="checkout__right-column">
            <OrderSummary
              packages={packages}
              currency={currency}
              totals={totals}
              items={displayItems}
              orderLoading={orderLoading}
              onPlaceOrder={handlePlaceOrder}
              newsletterOptIn={newsletterOptInGuest}
              onNewsletterOptInChange={setNewsletterOptInGuest}
              showNewsletterCheckbox={true}
              speedConfirmed={speedConfirmed}
              onSpeedConfirmChange={setSpeedConfirmed}
              isGuest={true}
            />
          </div>
        </>
      )}

      <PaymentModal
        isOpen={showPaymentModal}
        type={status}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentConfirm}
        wallet={wallet}
        loading={orderLoading}
        onlyWallet={isTopUpOrder}
      />

      <StatusModal
        isOpen={showStatusModal}
        type={finalStatus}
        isLoggedIn={isLoggedIn}
        customMessage={errorMessage}
        onClose={() => {
          if (finalStatus === "success") {
            clear();
          }
          setShowStatusModal(false);
          setFinalStatus(null);
          setErrorMessage("");
        }}
      />
    </section>
  );
};

export default CheckoutLayout;
